import { useEffect, useState } from "react";
import { api, formatINR, monthName } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { PageHeader, PageBody, Section, StatCell } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DownloadSimple, Plus, FilePdf, Receipt, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import AttendanceCalendar from "../components/AttendanceCalendar";
import { useAuth } from "../context/AuthContext";

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const YEARS = [2024, 2025, 2026];

export default function Payroll() {
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", month: filter.month, year: filter.year, bonus: 0, incentives: 0, leave_deduction: 0, late_penalty: 0 });
  const [calendarEmp, setCalendarEmp] = useState("");

  const load = async () => {
    if (!activeCompanyId) return;
    const [p, e] = await Promise.all([
      api.get("/payroll/payslips", { params: { company_id: activeCompanyId, month: filter.month, year: filter.year } }),
      api.get("/employees", { params: { company_id: activeCompanyId, limit: 500 } }),
    ]);
    setPayslips(p.data);
    setEmployees(e.data.items || []);
    if (!calendarEmp) {
      if (user?.role === "employee" && user?.employee_id) {
        setCalendarEmp(user.employee_id);
      } else {
        const me = (e.data.items || []).find(x => x.id === user?.employee_id) || (e.data.items || [])[0];
        if (me) setCalendarEmp(me.id);
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId, filter]);

  const generate = async () => {
    try {
      await api.post("/payroll/generate-payslip", { ...form, company_id: activeCompanyId });
      toast.success("Payslip generated");
      setOpen(false);
      setForm({ employee_id: "", month: filter.month, year: filter.year, bonus: 0, incentives: 0, leave_deduction: 0, late_penalty: 0 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const downloadPdf = async (id) => {
    const res = await api.get(`/payroll/payslips/${id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `payslip_${id}.pdf`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportExcel = async () => {
    const res = await api.get("/payroll/export-excel", { params: { company_id: activeCompanyId, month: filter.month, year: filter.year }, responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `payroll_${activeCompany?.code}_${filter.month}_${filter.year}.xlsx`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));
  const total = payslips.reduce((s, p) => s + (p.net || 0), 0);

  return (
    <div data-testid="payroll-page">
      <PageHeader eyebrow={activeCompany?.code} title="Payroll" subtitle="Generate payslips, manage salaries and export reports.">
        <Button variant="outline" onClick={exportExcel} className="rounded-sm text-xs uppercase tracking-wider" data-testid="payroll-export">
          <DownloadSimple size={14} className="mr-1.5" /> Excel
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="generate-payslip-btn">
              <Plus size={14} className="mr-1.5" /> Generate payslip
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>Generate payslip</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs uppercase">Employee</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
                  <SelectTrigger className="rounded-sm" data-testid="payslip-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase">Month</Label>
                  <Select value={String(form.month)} onValueChange={(v) => setForm({...form, month: Number(v)})}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={String(m)}>{monthName(m)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase">Year</Label>
                  <Select value={String(form.year)} onValueChange={(v) => setForm({...form, year: Number(v)})}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase">Bonus</Label><Input type="number" value={form.bonus} onChange={(e) => setForm({...form, bonus: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">Incentives</Label><Input type="number" value={form.incentives} onChange={(e) => setForm({...form, incentives: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">Leave deduction</Label><Input type="number" value={form.leave_deduction} onChange={(e) => setForm({...form, leave_deduction: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">Late penalty</Label><Input type="number" value={form.late_penalty} onChange={(e) => setForm({...form, late_penalty: e.target.value})} className="rounded-sm" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={generate} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="submit-payslip">Generate</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>
      <PageBody className="space-y-6">
        <div className="flex gap-3 items-end bg-white border border-border p-3 rounded-sm flex-wrap">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">Month</Label>
            <Select value={String(filter.month)} onValueChange={(v) => setFilter({...filter, month: Number(v)})}>
              <SelectTrigger className="rounded-sm w-36" data-testid="payroll-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={String(m)}>{monthName(m)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">Year</Label>
            <Select value={String(filter.year)} onValueChange={(v) => setFilter({...filter, year: Number(v)})}>
              <SelectTrigger className="rounded-sm w-28" data-testid="payroll-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            className="rounded-sm text-xs uppercase tracking-wider"
            onClick={() => {
              const now = new Date();
              const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
              setFilter({ month: prev.getMonth() + 1, year: prev.getFullYear() });
            }}
            data-testid="last-month-btn"
          >Last month</Button>
          <Button
            variant="outline"
            className="rounded-sm text-xs uppercase tracking-wider"
            onClick={() => {
              const now = new Date();
              setFilter({ month: now.getMonth() + 1, year: now.getFullYear() });
            }}
            data-testid="this-month-btn"
          >This month</Button>
        </div>

        <Section title={<><CalendarBlank size={14} className="inline mr-1.5"/> Attendance calendar — {monthName(filter.month)} {filter.year}</>}>
          {user?.role !== "employee" && (
            <div className="mb-3">
              <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">Employee</Label>
              <Select value={calendarEmp} onValueChange={setCalendarEmp}>
                <SelectTrigger className="rounded-sm w-72 mt-1" data-testid="calendar-emp-select"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {user?.role === "employee" && !user?.employee_id && (
            <div className="text-xs text-[#946200] bg-[#FFF7E0] border border-[#FAB005] p-3 rounded-sm">
              Your account is not linked to an employee record yet. Calendar will appear once HR links your login.
            </div>
          )}
          {(calendarEmp || user?.role === "employee") && (
            <div className="max-w-md">
              <AttendanceCalendar
                employeeId={calendarEmp || user?.employee_id || ""}
                companyId={activeCompanyId}
                month={filter.month}
                year={filter.year}
              />
            </div>
          )}
        </Section>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border rounded-sm overflow-hidden">
          <StatCell label="Payslips" value={payslips.length} testId="payroll-count" />
          <StatCell label="Total Net" value={formatINR(total)} accent="text-[#12B886]" testId="payroll-total" />
          <StatCell label={`Period`} value={`${monthName(filter.month)} ${filter.year}`} />
        </div>

        <Section title="Payslips" dense>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Period</TableHead>
              <TableHead>Gross</TableHead><TableHead>Deductions</TableHead><TableHead>Net</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">PDF</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {payslips.map((p) => {
                const e = empMap[p.employee_id] || {};
                const ded = p.pf + p.tax + p.leave_deduction + p.late_penalty + (p.other_deductions || 0);
                return (
                  <TableRow key={p.id} data-testid={`payslip-${p.id}`}>
                    <TableCell className="font-mono text-xs text-[#2563EB]">{e.employee_code}</TableCell>
                    <TableCell>{e.full_name || "—"}</TableCell>
                    <TableCell className="text-xs">{monthName(p.month)} {p.year}</TableCell>
                    <TableCell className="font-mono text-xs">{formatINR(p.gross)}</TableCell>
                    <TableCell className="font-mono text-xs text-[#F54236]">{formatINR(ded)}</TableCell>
                    <TableCell className="font-mono text-xs font-semibold text-[#12B886]">{formatINR(p.net)}</TableCell>
                    <TableCell><span className="text-[10px] uppercase tracking-wider font-semibold">{p.status}</span></TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => downloadPdf(p.id)} className="p-1.5 hover:bg-[#EFF6FF] rounded-sm" data-testid={`pdf-${p.id}`}><FilePdf size={16} /></button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {payslips.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-[#64748B] py-8">No payslips for this period</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Section>
      </PageBody>
    </div>
  );
}
